import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Maybe } from 'graphql/jsutils/Maybe';

import Rails from 'src/components/rails/Rails';
import {
  getThumbnailOptions,
  RailsSkeletonCount
} from 'src/components/rails/railsHelper';
import { Episode, Season } from 'src/services/GraphQL/Queries/types';
import { MediaBP as MQ } from 'src/styles/mediaQuery';
import { DynamicObject } from 'src/utils/commonTypes';
import { TAG_NAME } from 'src/utils/constants/TagNameConstants';
import { triggerMixpanelSelectDropdown } from 'src/utils/ConsumptionHelper/ConsumptionHelper';
import { getDeviceWidth } from 'src/utils/getDeviceWidth';

import { useAppStore } from 'src/hooks/useAppStore';
import { RAIL_TITLE_CONSTANTS } from '../../../../railTitleConstants';
import { SinglePlayResponse } from '../../../services/responseTypes/singlePlayResponse';
import { ConsumptionPageTypes } from '../../../types/consumption';
import { mixpanelObject } from '../../../utils/analytics';
import { MIXPANEL_SERIES } from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { API_STATUS_TYPES } from '../../../utils/constants/utilConstants';
import { IMAGE_PATH_AND_NAME } from '../../../utils/ImageHelper/ImagePathHelper';
import fetchNextPage from '../../../utils/IntersectionObserverHelper';
import SkeletonSimilarContentTray from '../../SkeletonLoader/SkeletonSimilarContentTray';
import { ThumbnailTypes } from '../../thumbnail/types';
import CollectionRails from './CollectionRails';

export type OtherCollectionType = {
  title: string;
  contents: any;
  moreLink: string;
  tagName?: string[];
};
export type RenderRailType = {
  showTitle: boolean;
  title?: string;
  contents: any;
  moreLink?: string;
  thumbnailType: ThumbnailTypes;
  tagName: string;
  isCollectionOfRails?: boolean;
  showTitleWithinThumbnail?: boolean;
} | null;
export interface Posts {
  category: string;
  excerpt: string;
  id: string;
  image_original: string;
  image_thumbnail: string;
  link: string;
  published_date: string;
  read_minutes: number;
  title: string;
}
interface RelatedContentsProps {
  similarContents: ConsumptionPageTypes.SimilarContent;
  recommendedContents: DynamicObject;
  type: ThumbnailTypes;
  otherCollections?: OtherCollectionType[];
  contentInstance: any;
  dropdownOptions: string[] | undefined;
  storyRailsData?: Posts[];
  tvShowTitle?: string;
  seasonLink?: string | null;
  singlePlayDetailsMovie?: SinglePlayResponse | undefined;
}
const RailContainer = styled.div`
  margin-top: 20px;
  padding-left: 16px;
  ${MQ.MediaSm} {
    padding-left: 0px;
  }
`;

const RelatedContents: React.FC<RelatedContentsProps> = ({
  similarContents,
  recommendedContents,
  type,
  otherCollections,
  contentInstance,
  dropdownOptions,
  storyRailsData,
  tvShowTitle,
  seasonLink,
  singlePlayDetailsMovie
}) => {
  const {
    appConfig: { isRequestedByBot }
  } = useAppStore();
  const [initialRails, setInitialRails] = useState<any[]>([]);
  const [isMounted, setMounted] = useState<boolean>(false);
  const [seoRails, setSeoRails] = useState<any[]>([]);
  const [intersectionStatus, setIntersectionStatus] = useState<any>({
    railType: 'initial',
    status: API_STATUS_TYPES.loading
  });

  const [updatedEpisodesRange, setUpdatedEpisodesRange] = useState<string>();
  const [updatedEpisodes, setUpdatedEpisodes] = useState<
    Maybe<Episode>[] | null
  >([]);

  const changeIntervalHandler = useCallback(
    async (index: number, selectedRange: string) => {
      setUpdatedEpisodesRange(selectedRange);
      if (similarContents?.seasons && similarContents.seasons.length > 1) {
        const episodes = similarContents.seasons.find(
          (season) => season.title === selectedRange
        )?.episodes;
        if (episodes) {
          setUpdatedEpisodes(episodes);
        }
      } else {
        try {
          const episodes =
            await contentInstance.contentInstance.fetchEpisodeContents(index);
          triggerMixpanelSelectDropdown(selectedRange, tvShowTitle);
          if (similarContents?.seasons?.length && index <= 2) {
            setUpdatedEpisodes(
              similarContents?.seasons[index - 1]?.episodes || []
            );
          }
          if (episodes?.contents) {
            setUpdatedEpisodes(episodes?.contents);
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    [contentInstance?.contentInstance, similarContents?.seasons, tvShowTitle]
  );

  const renderEpisodeBottomRails = () => {
    if (!similarContents?.seasons) {
      return null;
    }
    const sequenceOrder = [
      RAIL_TITLE_CONSTANTS.Webisodes,
      RAIL_TITLE_CONSTANTS.Clips,
      RAIL_TITLE_CONSTANTS.Promos,
      RAIL_TITLE_CONSTANTS.Mobisodes
    ];
    return sequenceOrder.map((title: string) => {
      return {
        showTitle: true,
        contents:
          similarContents?.seasons?.[0][title.toLowerCase() as keyof Season],
        tagName: TAG_NAME.EPISODES,
        thumbnailType: ThumbnailTypes.biggerHorizontal,
        title: title,
        moreLink: ''
      };
    });
  };

  const renderUpNextRail = () => {
    if (
      type === ThumbnailTypes.upnext &&
      (getDeviceWidth() as number) < 1200 &&
      recommendedContents?.[0]?.recommendedContentData
    ) {
      const { title, contents } = recommendedContents[0].recommendedContentData;
      return (
        <Rails
          showTitle={true}
          contents={contents}
          tagName={TAG_NAME.EPISODES}
          thumbnailType={ThumbnailTypes.horizontal}
          title={title}
          moreLink=""
          verticalIndex={0}
        />
      );
    } else {
      return null;
    }
  };

  const renderOtherCollections = () => {
    return otherCollections?.length
      ? otherCollections.map((collection) => {
          const [tagName, thumbnailType] = collection.tagName?.length
            ? getThumbnailOptions(collection.tagName)
            : [TAG_NAME.PORTRAIT_SMALL, ThumbnailTypes.vertical];
          return {
            showTitle: true,
            contents: collection?.contents,
            tagName,
            thumbnailType,
            title: collection.title,
            moreLink: collection.moreLink
          };
        })
      : null;
  };

  const renderStoryRelatedRails = () => {
    if (storyRailsData?.length) {
      return [
        {
          showTitle: true,
          contents: storyRailsData,
          tagName: TAG_NAME.EPISODES,
          thumbnailType: ThumbnailTypes.horizontal,
          title: `${tvShowTitle} Stories`,
          moreLink: ''
        }
      ];
    } else {
      return null;
    }
  };

  const generateRailsObject = (
    showTitle: boolean,
    contents: any,
    tagName: keyof typeof IMAGE_PATH_AND_NAME,
    thumbnailType: ThumbnailTypes,
    title = '',
    moreLink = '',
    isCollectionOfRails = false,
    showTitleWithinThumbnail = false
  ): RenderRailType => {
    return {
      showTitle,
      contents,
      tagName,
      thumbnailType,
      title,
      moreLink,
      isCollectionOfRails,
      showTitleWithinThumbnail
    };
  };

  const fetchSEORails = () => {
    let arrayOfSEORails: any[] = [];

    /* Tvshow Bottom Rails */
    if (renderEpisodeBottomRails()) {
      arrayOfSEORails = [
        ...arrayOfSEORails,
        ...(renderEpisodeBottomRails() || [])
      ];
    }

    /* Other collections */
    if (renderOtherCollections()) {
      arrayOfSEORails = [
        ...arrayOfSEORails,
        ...(renderOtherCollections() || [])
      ];
    }

    if (renderStoryRelatedRails()) {
      arrayOfSEORails = [
        ...arrayOfSEORails,
        ...(renderStoryRelatedRails() || [])
      ];
    }
    return arrayOfSEORails.filter((rail) => rail?.contents?.length);
  };

  const renderRails = (rails: any[]) => {
    return rails.map((rail, index) => {
      return (
        <Rails
          key={index}
          showTitle={rail.showTitle}
          contents={rail?.contents}
          tagName={rail?.tagName}
          thumbnailType={rail?.thumbnailType}
          title={rail?.title}
          moreLink={rail?.moreLink}
          verticalIndex={index}
          isCollectionResolved={false}
          showTitleWithinThumbnail={rail.showTitleWithinThumbnail}
        />
      );
    });
  };

  const renderEpisodeRails = () => {
    if (similarContents?.seasons) {
      let railsPropObject: any;
      const initialItems = similarContents.seasons[0]?.episodes?.slice(0, 10);
      if (updatedEpisodes?.length) {
        railsPropObject = generateRailsObject(
          false,
          updatedEpisodes,
          TAG_NAME.EPISODES,
          ThumbnailTypes.episode,
          '',
          seasonLink ? seasonLink : ''
        );
      } else {
        railsPropObject = generateRailsObject(
          false,
          initialItems,
          TAG_NAME.EPISODES,
          ThumbnailTypes.episode,
          '',
          seasonLink ? seasonLink : ''
        );
      }
      return (
        <Rails
          showTitle={railsPropObject.showTitle}
          contents={railsPropObject?.contents}
          tagName={railsPropObject?.tagName}
          thumbnailType={railsPropObject?.thumbnailType}
          title={railsPropObject?.title}
          moreLink={railsPropObject?.moreLink}
          verticalIndex={0}
          isCollectionResolved={false}
          dropdownOptions={!isRequestedByBot ? dropdownOptions : undefined}
          changeIntervalHandler={changeIntervalHandler}
          updatedEpisodesRange={updatedEpisodesRange}
        />
      );
    }
    return null;
  };

  const fetchInitialRails = () => {
    const sequenceOfRails: any[] = [];

    /* TV-Show Episodes */
    if (similarContents?.seasons) {
      const trailerItems = similarContents.seasons[0]?.trailers;
      if (trailerItems?.length) {
        sequenceOfRails.push(
          generateRailsObject(
            true,
            trailerItems,
            TAG_NAME.EPISODES,
            ThumbnailTypes.biggerHorizontal,
            RAIL_TITLE_CONSTANTS.Trailers,
            '',
            false,
            true
          )
        );
      }
      if (similarContents?.seasons?.[0]?.previews?.length) {
        sequenceOfRails.push(
          generateRailsObject(
            true,
            similarContents.seasons[0].previews,
            TAG_NAME.EPISODES,
            ThumbnailTypes.horizontal,
            RAIL_TITLE_CONSTANTS.Previews
          )
        );
      }
    }

    /* Movies You May Like */
    if (
      type !== ThumbnailTypes.upnext &&
      recommendedContents?.recommendedContentData?.length
    ) {
      sequenceOfRails.push(
        generateRailsObject(
          true,
          recommendedContents.recommendedContentData[0]?.contents,
          TAG_NAME.PORTRAIT_SMALL,
          ThumbnailTypes.vertical,
          recommendedContents.recommendedContentData[0]?.title
        )
      );
    }

    {
      /* Similar Movies */
    }
    if (similarContents?.relatedMovies?.contents?.length) {
      sequenceOfRails.push(
        generateRailsObject(
          true,
          similarContents?.relatedMovies?.contents,
          TAG_NAME.PORTRAIT_SMALL,
          ThumbnailTypes.vertical,
          similarContents.relatedMovies?.title
        )
      );
    }

    /* Related Videos */
    if (similarContents?.relatedVideos?.contents) {
      sequenceOfRails.push(
        generateRailsObject(
          true,
          similarContents.relatedVideos?.contents,
          TAG_NAME.LIST_BIG,
          ThumbnailTypes.horizontal,
          similarContents.relatedVideos?.title
        )
      );
    }

    return sequenceOfRails;
  };

  const allInitialRails = useMemo(() => fetchInitialRails(), []);
  const allSeoRails = useMemo(() => fetchSEORails(), []);

  // Set series title in mixpanel objectallSeoRails.slice(prevState.length, prevState.length + 2)
  useEffect(() => {
    if (tvShowTitle) {
      mixpanelObject[MIXPANEL_SERIES] = tvShowTitle;
    }
  }, [tvShowTitle]);

  const setRails = () => {
    setIntersectionStatus((prevState: any) => ({
      ...prevState,
      status: API_STATUS_TYPES.loading
    }));
    if (
      initialRails.length === allInitialRails.length ||
      intersectionStatus.railType == 'collection'
    ) {
      setIntersectionStatus((prevState: any) => ({
        ...prevState,
        status: API_STATUS_TYPES.success
      }));
      return fetchNextPage(() => ({}), true, 'initialRailsContainer');
    }

    setInitialRails((prevState) => [
      ...prevState,
      ...allInitialRails.slice(prevState.length, prevState.length + 2)
    ]);
    setIntersectionStatus((prevState: any) => ({
      ...prevState,
      status: API_STATUS_TYPES.success
    }));
  };

  const setRailsForSeo = () => {
    setIntersectionStatus({
      ...intersectionStatus,
      status: API_STATUS_TYPES.loading
    });
    if (
      seoRails.length === allSeoRails.length &&
      intersectionStatus.railType === 'seoRails'
    ) {
      setIntersectionStatus((prevState: any) => ({
        ...prevState,
        status: API_STATUS_TYPES.success
      }));
      return fetchNextPage(() => ({}), true, 'seoRailsContainer');
    }
    setSeoRails((prevState) => [
      ...prevState,
      ...allSeoRails.slice(prevState.length, prevState.length + 2)
    ]);
    setIntersectionStatus({
      ...intersectionStatus,
      status: API_STATUS_TYPES.success
    });
  };

  // Set series title in mixpanel object
  useEffect(() => {
    setMounted(true);
    if (initialRails.length === 0) {
      fetchNextPage(setRails, false, 'initialRailsContainer');
    }
  }, []);

  useEffect(() => {
    const dropdownValue = similarContents?.seasons?.find(
      (season) => season?.id === singlePlayDetailsMovie?.assetDetails?.season
    );
    let episodesRange = '';
    if (dropdownValue) {
      episodesRange =
        dropdownOptions?.find((option) => option === dropdownValue.title) || '';
    }
    // Reset state when similar content changes
    if (isMounted) {
      setInitialRails([]);
      setSeoRails([]);
      setIntersectionStatus({
        railType: 'initial',
        status: API_STATUS_TYPES.loading
      });
    }
    if (updatedEpisodesRange !== episodesRange) {
      setUpdatedEpisodesRange(episodesRange);
      setUpdatedEpisodes(dropdownValue?.episodes || []);
    }
  }, [similarContents]);

  // Remove intersection observer
  useEffect(() => {
    if (initialRails.length === allInitialRails.length) {
      fetchNextPage(setRails, true, 'initialRailsContainer');
      setIntersectionStatus({ railType: 'collection' });
    } else if (initialRails.length > 0) {
      fetchNextPage(setRails, false, 'initialRailsContainer');
    }
  }, [initialRails]);

  useEffect(() => {
    if (
      seoRails.length == 0 &&
      intersectionStatus.railType === 'seoRails' &&
      intersectionStatus.status === API_STATUS_TYPES.loading
    ) {
      fetchNextPage(setRailsForSeo, false, 'seoRailsContainer');
      setIntersectionStatus({ status: API_STATUS_TYPES.success });
    }
  }, [intersectionStatus.railType]);

  useEffect(() => {
    if (
      seoRails.length === allSeoRails.length &&
      intersectionStatus.railType === 'seoRails'
    ) {
      fetchNextPage(setRailsForSeo, true, 'seoRailsContainer');
      setIntersectionStatus((prevState: any) => ({
        ...prevState,
        status: API_STATUS_TYPES.success
      }));
    } else if (seoRails.length > 0) {
      fetchNextPage(setRailsForSeo, false, 'seoRailsContainer');
    }
  }, [seoRails]);

  const updateIntersectionStatus = () => {
    setIntersectionStatus({
      railType: 'seoRails',
      status: API_STATUS_TYPES.loading
    });
  };

  return (
    <RailContainer>
      {renderUpNextRail()}
      {renderEpisodeRails()}

      <section className="initialRailsContainer">
        {renderRails(initialRails)}
        {intersectionStatus.railType === 'initial' &&
        intersectionStatus.status == API_STATUS_TYPES.loading ? (
          <SkeletonSimilarContentTray railCount={RailsSkeletonCount.TWO} />
        ) : null}
      </section>
      {intersectionStatus.railType !== 'initial' && (
        <CollectionRails
          similarContents={similarContents}
          contentInstance={contentInstance}
          type={type}
          updateIntersectionStatus={updateIntersectionStatus}
        />
      )}
      <section className="seoRailsContainer">
        {renderRails(seoRails)}
        {intersectionStatus.railType === 'seoRails' &&
        intersectionStatus.status == API_STATUS_TYPES.loading ? (
          <SkeletonSimilarContentTray railCount={RailsSkeletonCount.TWO} />
        ) : null}
      </section>
    </RailContainer>
  );
};

export default React.memo(RelatedContents);
